/**
 * Hook for managing the admin interviewer setup page.
 *
 * Handles listing, creating, and deleting interviewer links,
 * plus file upload helpers for PDF/video attachments.
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import onboardingService from '@/lib/api/onboarding';
import type {
  FileUploadInfo,
  Interviewer,
  ReingestMode,
  RemoveInterviewerKnowledgePayload,
  ResolveOpenQuestionPayload,
  SourceRemovalPreview,
} from '@/lib/api/onboarding';

export function useInterviewerSetup(organizationId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['interviewers', organizationId], [organizationId]);

  // Fetch interviewers
  const {
    data: listData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await onboardingService.listInterviewers(organizationId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch interviewers');
      }
      return response.data;
    },
    enabled: !!organizationId,
  });

  const interviewers = listData?.interviewers ?? [];
  const primaryInterviewer = interviewers.find((interviewer) => interviewer.is_primary) ?? interviewers[0] ?? null;
  const error = queryError instanceof Error ? queryError.message : null;

  // Create interviewer mutation
  const createMutation = useMutation({
    mutationFn: async ({
      websiteUrl,
      knowledgeBaseUrl,
      fileUploads,
      name,
      selectedVideoIds,
      knowledgeAssetIds,
    }: {
      websiteUrl?: string;
      knowledgeBaseUrl?: string;
      fileUploads?: FileUploadInfo[];
      name?: string;
      selectedVideoIds?: string[];
      knowledgeAssetIds?: string[];
    }) => {
      const response = await onboardingService.createInterviewer(
        organizationId,
        websiteUrl,
        knowledgeBaseUrl,
        fileUploads,
        name,
        selectedVideoIds,
        knowledgeAssetIds
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create interviewer');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: data.reused_existing ? 'Primary Training Updated' : 'Primary Training Created',
        description: data.reused_existing
          ? 'New knowledge was attached to your existing training workspace.'
          : `Training is ready for ${data.name || 'your agent'}.`,
      });
    },
    onError: (err: Error) => {
      toast({
        variant: 'destructive',
        title: 'Creation Failed',
        description: err.message,
      });
    },
  });

  const ingestMutation = useMutation({
    mutationFn: async ({
      interviewerId,
      websiteUrl,
      knowledgeBaseUrl,
      fileUploads,
      selectedVideoIds,
      knowledgeAssetIds,
    }: {
      interviewerId: string;
      websiteUrl?: string;
      knowledgeBaseUrl?: string;
      fileUploads?: FileUploadInfo[];
      selectedVideoIds?: string[];
      knowledgeAssetIds?: string[];
    }) => {
      const response = await onboardingService.ingestInterviewerKnowledge(interviewerId, {
        website_url: websiteUrl,
        knowledge_base_url: knowledgeBaseUrl,
        file_uploads: fileUploads,
        selected_video_ids: selectedVideoIds,
        knowledge_asset_ids: knowledgeAssetIds,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to attach knowledge');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['interviewer', variables.interviewerId] });
      toast({
        title: 'Knowledge Attached',
        description: 'A new ingestion batch has started for this training session.',
      });
    },
    onError: (err: Error) => {
      toast({
        variant: 'destructive',
        title: 'Attach Failed',
        description: err.message,
      });
    },
  });

  // Delete interviewer mutation
  const deleteMutation = useMutation({
    mutationFn: async (interviewerId: string) => {
      const response = await onboardingService.deleteInterviewer(interviewerId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete interviewer');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Training Session Deleted' });
    },
    onError: (err: Error) => {
      toast({
        variant: 'destructive',
        title: 'Delete Failed',
        description: err.message,
      });
    },
  });

  const reingestMutation = useMutation({
    mutationFn: async ({
      interviewerId,
      mode,
    }: {
      interviewerId: string;
      mode?: ReingestMode;
    }) => {
      const response = await onboardingService.reingestInterviewer(
        interviewerId,
        mode || 'full_rebuild'
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to reingest interviewer');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['interviewer', variables.interviewerId] });
      toast({
        title: 'Reingest Started',
        description: 'Existing sources are being reprocessed and canonical knowledge is rebuilding.',
      });
    },
    onError: (err: Error) => {
      toast({
        variant: 'destructive',
        title: 'Reingest Failed',
        description: err.message,
      });
    },
  });

  const resolveOpenQuestionsMutation = useMutation({
    mutationFn: async ({
      interviewerId,
      resolutions,
    }: {
      interviewerId: string;
      resolutions: ResolveOpenQuestionPayload[];
    }) => {
      const response = await onboardingService.resolveOpenQuestions(
        interviewerId,
        resolutions
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to resolve open questions');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['interviewer', variables.interviewerId] });
      toast({
        title: 'Open Questions Updated',
        description: 'Your answers were merged into canonical training knowledge.',
      });
    },
    onError: (err: Error) => {
      toast({
        variant: 'destructive',
        title: 'Update Failed',
        description: err.message,
      });
    },
  });

  const consolidateInterviewersMutation = useMutation({
    mutationFn: async ({
      interviewerId,
      sourceInterviewerIds,
    }: {
      interviewerId: string;
      sourceInterviewerIds: string[];
    }) => {
      const response = await onboardingService.consolidateInterviewers(
        interviewerId,
        sourceInterviewerIds
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to consolidate interviewers');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['interviewer', variables.interviewerId] });
      toast({
        title: 'Legacy Training Consolidated',
        description: 'Historical interviewer records were merged into the primary training workspace.',
      });
    },
    onError: (err: Error) => {
      toast({
        variant: 'destructive',
        title: 'Consolidation Failed',
        description: err.message,
      });
    },
  });

  const previewRemovalMutation = useMutation({
    mutationFn: async ({
      interviewerId,
      payload,
    }: {
      interviewerId: string;
      payload: RemoveInterviewerKnowledgePayload;
    }) => {
      const response = await onboardingService.previewInterviewerKnowledgeRemoval(
        interviewerId,
        payload
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to preview source removal');
      }
      return response.data as SourceRemovalPreview;
    },
  });

  const removeKnowledgeMutation = useMutation({
    mutationFn: async ({
      interviewerId,
      payload,
    }: {
      interviewerId: string;
      payload: RemoveInterviewerKnowledgePayload;
    }) => {
      const response = await onboardingService.removeInterviewerKnowledge(
        interviewerId,
        payload
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to remove source');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['interviewer', variables.interviewerId] });
      toast({
        title: 'Source Removal Started',
        description: 'The training workspace is rebuilding without that source.',
      });
    },
    onError: (err: Error) => {
      toast({
        variant: 'destructive',
        title: 'Removal Failed',
        description: err.message,
      });
    },
  });
  const previewRemovalMutateAsync = previewRemovalMutation.mutateAsync;
  const removeKnowledgeMutateAsync = removeKnowledgeMutation.mutateAsync;

  /**
   * Upload a file for an interviewer: request signed URL, then upload.
   * Returns the FileUploadInfo on success, or null on failure.
   */
  const uploadFile = useCallback(
    async (file: File): Promise<FileUploadInfo | null> => {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      const videoExts = ['mp4', 'mov', 'webm', 'avi', 'mkv'];
      const videoMimeTypes = [
        'video/mp4',
        'video/quicktime',
        'video/webm',
        'video/x-msvideo',
        'video/x-matroska',
      ];

      let fileType: 'pdf' | 'video' | null = null;
      if (ext === 'pdf' || file.type === 'application/pdf') {
        fileType = 'pdf';
      } else if (videoExts.includes(ext) || videoMimeTypes.includes(file.type)) {
        fileType = 'video';
      }

      if (!fileType) {
        toast({
          variant: 'destructive',
          title: 'Unsupported file type',
          description: `${file.name}: only PDF or video files are supported.`,
        });
        return null;
      }

      try {
        // 1. Request signed upload URL
        const urlResponse = await onboardingService.requestInterviewerUploadUrl(
          organizationId,
          file.name,
          fileType
        );
        if (!urlResponse.success || !urlResponse.data) {
          throw new Error(urlResponse.error || 'Failed to get upload URL');
        }

        const { storage_path, upload_url } = urlResponse.data;

        // 2. Upload file to signed URL
        const uploaded = await onboardingService.uploadFileToSignedUrl(upload_url, file);
        if (!uploaded) {
          throw new Error('File upload failed');
        }

        return {
          storage_path,
          filename: file.name,
          file_type: fileType,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        toast({
          variant: 'destructive',
          title: 'Upload Failed',
          description: `${file.name}: ${message}`,
        });
        return null;
      }
    },
    [organizationId, toast]
  );

  const createInterviewer = useCallback(
    async (
      websiteUrl?: string,
      knowledgeBaseUrl?: string,
      fileUploads?: FileUploadInfo[],
      name?: string,
      selectedVideoIds?: string[],
      knowledgeAssetIds?: string[]
    ): Promise<Interviewer | null> => {
      try {
        return await createMutation.mutateAsync({
          websiteUrl,
          knowledgeBaseUrl,
          fileUploads: fileUploads?.length ? fileUploads : undefined,
          name,
          selectedVideoIds: selectedVideoIds?.length ? selectedVideoIds : undefined,
          knowledgeAssetIds: knowledgeAssetIds?.length ? knowledgeAssetIds : undefined,
        });
      } catch {
        return null;
      }
    },
    [createMutation]
  );

  const deleteInterviewer = useCallback(
    async (interviewerId: string): Promise<boolean> => {
      try {
        await deleteMutation.mutateAsync(interviewerId);
        return true;
      } catch {
        return false;
      }
    },
    [deleteMutation]
  );

  const ingestKnowledge = useCallback(
    async (
      interviewerId: string,
      {
        websiteUrl,
        knowledgeBaseUrl,
        fileUploads,
        selectedVideoIds,
        knowledgeAssetIds,
      }: {
        websiteUrl?: string;
        knowledgeBaseUrl?: string;
        fileUploads?: FileUploadInfo[];
        selectedVideoIds?: string[];
        knowledgeAssetIds?: string[];
      }
    ): Promise<Interviewer | null> => {
      try {
        return await ingestMutation.mutateAsync({
          interviewerId,
          websiteUrl,
          knowledgeBaseUrl,
          fileUploads: fileUploads?.length ? fileUploads : undefined,
          selectedVideoIds: selectedVideoIds?.length ? selectedVideoIds : undefined,
          knowledgeAssetIds: knowledgeAssetIds?.length ? knowledgeAssetIds : undefined,
        });
      } catch {
        return null;
      }
    },
    [ingestMutation]
  );

  const reingestInterviewer = useCallback(
    async (
      interviewerId: string,
      mode: ReingestMode = 'full_rebuild'
    ): Promise<Interviewer | null> => {
      try {
        return await reingestMutation.mutateAsync({ interviewerId, mode });
      } catch {
        return null;
      }
    },
    [reingestMutation]
  );

  const resolveOpenQuestions = useCallback(
    async (
      interviewerId: string,
      resolutions: ResolveOpenQuestionPayload[]
    ): Promise<Interviewer | null> => {
      try {
        return await resolveOpenQuestionsMutation.mutateAsync({
          interviewerId,
          resolutions,
        });
      } catch {
        return null;
      }
    },
    [resolveOpenQuestionsMutation]
  );

  const consolidateInterviewers = useCallback(
    async (
      interviewerId: string,
      sourceInterviewerIds: string[]
    ): Promise<Interviewer | null> => {
      try {
        return await consolidateInterviewersMutation.mutateAsync({
          interviewerId,
          sourceInterviewerIds,
        });
      } catch {
        return null;
      }
    },
    [consolidateInterviewersMutation]
  );

  const previewKnowledgeRemoval = useCallback(
    async (
      interviewerId: string,
      payload: RemoveInterviewerKnowledgePayload
    ): Promise<SourceRemovalPreview | null> => {
      try {
        return await previewRemovalMutateAsync({ interviewerId, payload });
      } catch {
        return null;
      }
    },
    [previewRemovalMutateAsync]
  );

  const removeKnowledge = useCallback(
    async (
      interviewerId: string,
      payload: RemoveInterviewerKnowledgePayload
    ): Promise<Interviewer | null> => {
      try {
        return await removeKnowledgeMutateAsync({ interviewerId, payload });
      } catch {
        return null;
      }
    },
    [removeKnowledgeMutateAsync]
  );

  const refreshInterviewers = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const copyLink = useCallback(
    async (interviewer: Interviewer) => {
      const link =
        interviewer.interview_link ||
        `${window.location.origin}/interview/${interviewer.token}`;
      try {
        await navigator.clipboard.writeText(link);
        toast({ title: 'Link Copied', description: 'Interview link copied to clipboard.' });
      } catch {
        // Fallback for when clipboard API is unavailable
        const textarea = document.createElement('textarea');
        textarea.value = link;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        toast({ title: 'Link Copied', description: 'Interview link copied to clipboard.' });
      }
    },
    [toast]
  );

  return {
    interviewers,
    primaryInterviewer,
    isLoading,
    isCreating: createMutation.isPending,
    isIngesting: ingestMutation.isPending,
    isReingesting: reingestMutation.isPending,
    isResolvingOpenQuestions: resolveOpenQuestionsMutation.isPending,
    isConsolidating: consolidateInterviewersMutation.isPending,
    isPreviewingKnowledgeRemoval: previewRemovalMutation.isPending,
    isRemovingKnowledge: removeKnowledgeMutation.isPending,
    isDeleting: deleteMutation.isPending,
    error,

    createInterviewer,
    ingestKnowledge,
    reingestInterviewer,
    resolveOpenQuestions,
    consolidateInterviewers,
    previewKnowledgeRemoval,
    removeKnowledge,
    deleteInterviewer,
    refreshInterviewers,
    copyLink,
    uploadFile,
  };
}
